import type { Bounds, FederatedPointerEvent, Point } from 'pixi.js';
import { Graphics, Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import { KnotBlockerModel } from 'game/boardMVC/models/blockers/KnotBlockerModel';
import { FXParticleLayer } from '../../../../libs/particles/FXParticleLayer';
import type { BoardModel } from '../../models/board/BoardModel';
import type { TileStackView } from './TileStackView';
import type { TileStackModel } from '../../models/board/TileStackModel';
import { BoardEventType } from '../../models/board/BoardEvents';
import { gridPositionToPxPosition } from '../../utils/gridPositionToPxPosition';
import type { BoardViewFactory } from './BoardViewFactory';
import { GAME_OBJECT_POOL } from '../../../GameConstants';
import { globalToGridPos } from '../../utils/globalToGridPos';
import { BoardBackgroundLayer } from './BoardBackgroundLayer';
import type { TileView } from './TileView';
import { ConnectionStatus } from '../../types';
import type { GridPosition, BlockerView } from '../../types';
import { BoardHighlightLayer } from './BoardHighlightLayer';
import { TileFlipAnimator } from './TileFlipAnimator';
import { TileRemovalAnimator } from './TileRemovalAnimator';
import { KnotBlockerView } from '../blockers/KnotBlockerView';

type TileStackId = string;

export type StackViewProps = { x: number; y: number; zIndex: number };

export class BoardView extends Container {
  public readonly onAnimationFinished = new Signal<(id: string) => void>();

  public readonly onStackPointerDown = new Signal<(stack: TileStackModel, view: TileStackView) => void>();

  public readonly onStackTapped = new Signal<(stack: TileStackModel, view: TileStackView) => void>();

  public readonly highlightLayer: BoardHighlightLayer;

  public readonly background = new BoardBackgroundLayer().props({
    parent: this,
  });

  private readonly container = new Container().props({
    parent: this,
  });

  public readonly initialBounds: Bounds;

  public readonly initialBoundsWithTiles: Bounds;

  private readonly stackContainer = new Container().props({
    parent: this.container,
  });

  public readonly particleLayer = new FXParticleLayer(true).props({
    parent: this.container,
  });

  private readonly tileMoveAnimator = new TileFlipAnimator(this.stackContainer);

  private readonly tileRemovalAnimator = new TileRemovalAnimator({
    stackContainer: this.stackContainer,
    particleLayer: this.particleLayer,
  });

  constructor(
    private readonly model: BoardModel,
    private readonly viewFactory: BoardViewFactory,
  ) {
    super();
    // this.addIdleVisualiser();

    this.highlightLayer = new BoardHighlightLayer(this);

    this.background.fillTiles(model);

    this.initialBounds = this.background.getLocalBounds();
    // to make 0,0 at top left of the view
    this.pivot.set(this.initialBounds.x, this.initialBounds.y);

    this.fillView();

    this.initialBoundsWithTiles = this.getLocalBounds();

    this.addModelListeners();
    this.addGridPointerEvents();
    this.stackContainer.sortableChildren = true;
  }

  // private addIdleVisualiser(): void {
  //   const gfx = new Graphics().props({
  //     parent: this,
  //     y: -100,
  //   });
  //
  //   const gfx2 = new Graphics().props({
  //     parent: this,
  //     y: -90,
  //   });
  //
  //   const update = (): void => {
  //     gfx.clear();
  //     gfx.rect(0, 0, 80, 10);
  //     gfx.fill(this.model.isIdle() ? 0x00ff00 : 0xff0000);
  //
  //     gfx2.clear();
  //     gfx2.rect(0, 0, 80, 10);
  //     gfx2.fill(this.model.isIdle2() ? 0x00ff00 : 0xff0000);
  //   };
  //
  //   this.pixi.ticker.add(update);
  //   this.on('destroyed', () => this.pixi.ticker.remove(update));
  // }

  public reset(): void {
    this.model.getStacks().forEach((model) => {
      const view = this.viewFactory.getStackView(model.id);
      if (view) view.destroy({ children: true });
      this.createTileStackView(model);
    });
  }

  public debugDrawBoundingBox(color: number | string): void {
    const gfx = new Graphics();
    gfx.strokeStyle = { width: 2, color };
    gfx.rect(this.initialBounds.x, this.initialBounds.y, this.initialBounds.width, this.initialBounds.height);
    gfx.stroke();
    this.addChild(gfx);
  }

  public getModel(): BoardModel {
    return this.model;
  }

  private addModelListeners(): void {
    const { model } = this;

    model.events.addListener(BoardEventType.MoveMade, ({ stackId }) => {
      this.highlightLayer.setSelectionHighlight(null, true);
      this.resetStackView(stackId);
    });

    model.events.addListener(BoardEventType.TileMoved, async ({ fromStackId, toStackId, tileIds, time, id }) => {
      await this.animateTilesMove(fromStackId, toStackId, tileIds, time);
      this.onAnimationFinished.emit(id);
    });

    model.events.addListener(BoardEventType.TilesRemoved, async ({ stackId, tileIds, id, reason }) => {
      const stackView = this.getStackView(stackId);
      if (!stackView) {
        this.onAnimationFinished.emit(id);
        return;
      }

      await this.wait(50);

      await this.tileRemovalAnimator.animateTileRemoval({
        stackView,
        tileIds,
        reason,
      });

      this.onAnimationFinished.emit(id);
    });

    model.events.addListener(BoardEventType.BlockerRemoved, async ({ id, blockerId, stackId }) => {
      const stackModel = this.getStackView(stackId)?.model;

      if (!stackModel) return;

      const blockerView = this.viewFactory.getBlockerView(blockerId);

      if (!blockerView) {
        this.onAnimationFinished.emit(id);
        return;
      }

      const promises = [];

      if (blockerView instanceof KnotBlockerView) {
        promises.push(this.animateKnotConnectionsOnRemoval(blockerView, stackModel));
      }

      promises.push(blockerView.animateRemoval());

      void Promise.all(promises).then(() => {
        this.onAnimationFinished.emit(id);
      });
    });

    model.events.addListener(BoardEventType.BlockerChanged, async ({ stackId, blockerId }) => {
      const blockerView = this.viewFactory.getBlockerView(blockerId);
      if (!blockerView) return;

      blockerView.onModelChanged?.();

      if (blockerView instanceof KnotBlockerView) {
        const stackView = this.getStackView(stackId);

        if (stackView) {
          await this.animateConnectedKnotBlockersOnHit(blockerView, stackView);
        }
      }
    });

    model.events.addListener(BoardEventType.StacksSwapped, async ({ stackId, otherStackId }) => {
      this.resetStackView(stackId);
      this.resetStackView(otherStackId);
    });
  }

  public async animateTilesMove(
    fromStackId: string,
    toStackId: string,
    tilesId: string[],
    _duration: number,
  ): Promise<void> {
    const fromStackView = this.getStackView(fromStackId);
    const targetStackView = this.getStackView(toStackId);

    if (!fromStackView || !targetStackView) return;

    const tileViews: TileView[] = [];

    tilesId.reverse().forEach((tileId) => {
      const view = this.getTileView(tileId);
      if (!view) return;
      tileViews.push(view);
    });

    await this.tileMoveAnimator.animateTileFlip(tileViews, fromStackView, targetStackView);
  }

  public async animateHammerHit(): Promise<void> {
    const boardSlot = this.parent;
    await boardSlot?.tweenTo({ scaleX: 0.95, scaleY: 0.95 }, 150, 'quadOut', true).asyncOnComplete();
    await boardSlot?.tweenTo({ scaleX: 1, scaleY: 1 }, 300, 'sinOut', true).asyncOnComplete();
  }

  private async animateConnectedKnotBlockersOnHit(
    blockerView: KnotBlockerView,
    stackView: TileStackView,
  ): Promise<void> {
    const blockerViewsToAnimate = [blockerView];

    const neighboringConnectedStacks = stackView?.model.getConnectedBlockerStacks(true);

    neighboringConnectedStacks?.forEach((connectedStack) => {
      if (!connectedStack.blocker) return;

      const connectedBlockerView = this.viewFactory.getBlockerView(connectedStack.blocker.id) as KnotBlockerView;
      if (!connectedBlockerView) return;

      blockerViewsToAnimate.push(connectedBlockerView);
    });

    const origin = { x: stackView.model.x, y: stackView.model.y } as GridPosition;

    await Promise.all(blockerViewsToAnimate.map((viewToAnimate) => viewToAnimate.animateRopesOnHit(origin)));
  }

  private async animateKnotConnectionsOnRemoval(
    blockerView: KnotBlockerView,
    stackModel: TileStackModel,
  ): Promise<void> {
    const neighbors = stackModel
      .getConnectedBlockerStacks(true)
      .filter((neighbor) => neighbor.blocker && neighbor.blocker.id !== stackModel.blocker?.id);

    const promises = [];

    const soonToBeRemovedBlockerStacks = neighbors.filter(
      (neighbor) =>
        neighbor.blocker &&
        neighbor.blocker instanceof KnotBlockerModel &&
        neighbor.blocker.connection.status === ConnectionStatus.WillBeRemoved,
    );

    // remove ropes between blockers that are going to be removed at the same instant
    soonToBeRemovedBlockerStacks.forEach((neighbor) => {
      if (!neighbor.blocker) return;
      promises.push(blockerView.animateCutRopes({ x: neighbor.x, y: neighbor.y }));
    });

    // remove ropes from the blocker that is being removed
    promises.push(blockerView?.animateRetractRopes({ x: stackModel.x, y: stackModel.y }));

    // remove ropes from the direct neighbors of the blocker that is being removed
    neighbors.forEach((neighbor) => {
      if (!neighbor.blocker) return;
      const blocker = neighbor.blocker as KnotBlockerModel;

      if (blocker.connection.status !== ConnectionStatus.Connected) return;

      const neighborBlockerView = this.viewFactory.getBlockerView(blocker.id);
      if (neighborBlockerView instanceof KnotBlockerView) {
        promises.push(neighborBlockerView.animateRetractRopes({ x: stackModel.x, y: stackModel.y }, 100));
      }
    });
  }

  public resetStackView(stackId: string): void {
    const view = this.getStackView(stackId);
    if (!view) return;

    view.props(this.getStackViewProps(view.model));

    view.tiles.removeChildren();
    view.model.getTiles().forEach((tile) => {
      const tileView = this.viewFactory.getTileView(tile.id) ?? this.viewFactory.createTileView(tile);
      view.addTileView(tileView);
    });
  }

  public getStackView(stackId: TileStackId): TileStackView | null {
    return this.viewFactory.getStackView(stackId);
  }

  public getTileView(tileId: TileStackId): TileView | null {
    return this.viewFactory.getTileView(tileId) ?? null;
  }

  public getBlockerView(blockerId: string): BlockerView | null {
    return this.viewFactory.getBlockerView(blockerId) ?? null;
  }

  public getStackViewByGridXY(x: number, y: number): TileStackView | null {
    const stackId = this.model.getStack(x, y)?.id;
    return !stackId ? null : (this.getStackView(stackId) ?? null);
  }

  public fillView(): void {
    this.model.getStacks().forEach((model) => {
      this.createTileStackView(model);
    });
  }

  public getStackViewProps(gridPosition: { x: number; y: number }): StackViewProps {
    const { x, y } = gridPositionToPxPosition(gridPosition.x, gridPosition.y);
    return {
      x,
      y,
      zIndex: y * 1000 + (x % 2) * 100,
    };
  }

  public createTileStackView(model: TileStackModel): TileStackView {
    const stackView = this.viewFactory.createStackView(model).props({
      parent: this.stackContainer,
      ...this.getStackViewProps(model),
    });

    this.highlightLayer.createHighlight(model);
    return stackView;
  }

  public getStackContainer(): Container {
    return this.stackContainer;
  }

  public setSelectionHighlight(tileStackId: TileStackId | null, instant = false): void {
    this.highlightLayer.setSelectionHighlight(tileStackId, instant);
  }

  public get matchesAreAnimating(): boolean {
    return this.model.hasActiveMatches();
  }

  private addGridPointerEvents(): void {
    const { container } = this;
    container.interactive = true;

    const getStackAtPointerPos = ({ globalX, globalY }: FederatedPointerEvent): TileStackModel | null => {
      const globalPos = GAME_OBJECT_POOL.getPoint().set(globalX, globalY);
      const gridPos = globalToGridPos(container, globalPos);
      return this.model.getStack(gridPos.x, gridPos.y) ?? null;
    };

    container.on('pointertap', (e) => {
      const stack = getStackAtPointerPos(e);
      const view = stack ? this.getStackView(stack.id) : null;
      if (stack && view) this.onStackTapped.emit(stack, view);
    });

    container.on('pointerdown', (e) => {
      const stack = getStackAtPointerPos(e);
      const view = stack ? this.getStackView(stack.id) : null;
      if (stack && view) this.onStackPointerDown.emit(stack, view);
    });
  }

  public pointerToStack(globalPos: Point): TileStackModel | null {
    const { container } = this;
    const gridPos = globalToGridPos(container, globalPos);
    return this.model.getStack(gridPos.x, gridPos.y);
  }

  public killEvents(): void {
    this.onAnimationFinished.disconnectAll();
    this.onStackPointerDown.disconnectAll();
    this.onStackTapped.disconnectAll();
  }
}
